import {
  Button,
  FormSectionDivider,
  FormSectionHeader,
  FormToggleLabel,
  HandleDropDownListChange,
  SearchableChargeCalcDropdown,
  SearchableChargeTypeDropdown,
  SlideOutModal,
  SlideOutModalProps,
} from "../../../components";
import { Button as BSButton, Col, FloatingLabel, Form, Row } from "react-bootstrap";
import * as yup from 'yup';
import { Formik } from 'formik';
import { TenantService } from "../../../services";
import { sharedProperty, useTenantFeatures, useUrlParams } from "../../../hooks";
import { ChargeCalculation, ChargeType, ServiceModel } from "../../../data/entities";
import { useRef } from "react";
import ReactTextareaAutosize from "react-textarea-autosize";

export interface AddServiceModalProps extends SlideOutModalProps {
  showAddState: sharedProperty<boolean>;
  onSubmit: any;
}
interface ServiceFormValues {
  name: string;
  description: string;
  defaultPrice: number;
  defaultChargeAmount: number;
  defaultChargeCalculation: ChargeCalculation | null;
  defaultChargeType: ChargeType | null;
  hideProviders: boolean;
  defaultDuration: number;
  defaultBufferDuration: number;
}

export function AddServiceModal(props: Readonly<AddServiceModalProps>) {

  const id = parseInt(useUrlParams("id"));
  const submitRef = useRef<HTMLButtonElement>(null);
  const { managePricing } = useTenantFeatures();

  // Pricing fields are hidden when the tenant cannot manage pricing, so
  // they must not be required for the form to submit
  const validationSchema = yup.object().shape({
    name: yup.string().required("Name is required"),
    defaultPrice: managePricing
      ? yup.number().required("Default price is required").moreThan(0, "Price must be greater than 0")
      : yup.number().notRequired(),
    defaultChargeCalculation: managePricing
      ? yup.mixed<ChargeCalculation>().required("Charge calculation is required")
      : yup.mixed<ChargeCalculation>().nullable().notRequired(),
    defaultChargeType: managePricing
      ? yup.mixed<ChargeType>().required("Charge type is required")
      : yup.mixed<ChargeType>().nullable().notRequired(),
    defaultChargeAmount: managePricing
      ? yup.number().required("Amount is required").min(0, "Amount cannot be less than 0")
      : yup.number().notRequired(),
    defaultDuration: yup.number().required("Default duration is required"),
    defaultBufferDuration: yup.number().required("Default buffer duration is required"),
    description: yup.string().notRequired(),
    hideProviders: yup.boolean().notRequired(),
  });

  return (
    <SlideOutModal
      {...props}
      title="Add New Service"
      footer={
        <div className="container p-0">
          <div className="row g-0">
            <div className="col-12 pe-2">
              <Button onClick={() => props.showState.setter(false)} variant="cancel" className="w-100">
                Cancel
              </Button>
            </div>
            <div className="col-12 ps-2">
              <Button onClick={() => submitRef.current?.click()} variant="primary" className="w-100">
                Add
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <Formik<ServiceFormValues>
        validationSchema={validationSchema}
        initialValues={{
          name: '',
          description: '',
          defaultPrice: 0,
          defaultChargeAmount: 0,
          defaultChargeCalculation: null,
          defaultChargeType: null,
          hideProviders: true,
          defaultDuration: 60,
          defaultBufferDuration: 15
        }}
        onSubmit={async (values) => {
          const service = {
            ...values,
            tenantId: TenantService.getTenantId(),
            serviceTypeId: id,
            sku: `FAB${Math.floor(Math.random() * 10000)}`
          } as ServiceModel;
          props.onSubmit(service);
          props.showAddState.setter(false);
        }}
      >
        {({ values, setFieldValue, handleSubmit, handleChange, touched, errors }) => {
          return (
            <Form onSubmit={handleSubmit} className="px-2 pt-2">

              {/* Basics */}
              <FormSectionHeader>Basics</FormSectionHeader>
              <Row>
                <Col>
                  <Form.Group controlId="serviceName">
                    <FloatingLabel label="Name" className="mb-3">
                      <Form.Control
                        type="text"
                        name="name"
                        placeholder="Service Name"
                        value={values.name}
                        onChange={handleChange}
                        isInvalid={touched.name && !!errors.name}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group controlId="description">
                    <FloatingLabel label="Description" className="mb-3">
                      <ReactTextareaAutosize
                        minRows={3}
                        className="form-control"
                        placeholder="Description"
                        name="description"
                        value={values.description}
                        onChange={handleChange}
                      />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
              </Row>

              <FormSectionDivider />

              {/* Pricing */}
              <FormSectionHeader>Pricing</FormSectionHeader>
              {managePricing ? (
              <>
              <Row>
                <Col xs={24} md={12}>
                  <Form.Group controlId="defaultPrice">
                    <FloatingLabel label="Default Price" className="mb-3">
                      <Form.Control
                        type="number"
                        name="defaultPrice"
                        placeholder="Default Price"
                        value={values.defaultPrice}
                        onChange={handleChange}
                        isInvalid={touched.defaultPrice && !!errors.defaultPrice}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.defaultPrice}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Group controlId="defaultChargeType">
                    <SearchableChargeTypeDropdown
                      defaultItem={true}
                      name="defaultChargeType"
                      value={values.defaultChargeType}
                      placeholder="Charge Type"
                      onChange={(e) => HandleDropDownListChange(e, setFieldValue)}
                      isInvalid={touched.defaultChargeType && !!errors.defaultChargeType}
                    >
                      <Form.Control.Feedback type="invalid">
                        {errors.defaultChargeType}
                      </Form.Control.Feedback>
                    </SearchableChargeTypeDropdown>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={24} md={12}>
                  <Form.Group controlId="defaultChargeCalculation">
                    <SearchableChargeCalcDropdown
                      defaultItem={true}
                      name="defaultChargeCalculation"
                      value={values.defaultChargeCalculation}
                      onChange={(e) => HandleDropDownListChange(e, setFieldValue)}
                      isInvalid={touched.defaultChargeCalculation && !!errors.defaultChargeCalculation}
                    >
                      <Form.Control.Feedback type="invalid">
                        {errors.defaultChargeCalculation}
                      </Form.Control.Feedback>
                    </SearchableChargeCalcDropdown>
                  </Form.Group>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Group controlId="defaultChargeAmount">
                    <FloatingLabel label="Charge Amount" className="mb-3">
                      <Form.Control
                        type="number"
                        name="defaultChargeAmount"
                        placeholder="Charge Amount"
                        value={values.defaultChargeAmount}
                        onChange={handleChange}
                        isInvalid={touched.defaultChargeAmount && !!errors.defaultChargeAmount}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.defaultChargeAmount}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Form.Group>
                </Col>
              </Row>
              </>
              ) : (
                <p className="small text-body-secondary mb-3">
                  Pricing for this service is managed for you.
                </p>
              )}

              <FormSectionDivider />

              {/* Scheduling */}
              <FormSectionHeader>Scheduling</FormSectionHeader>
              <Row>
                <Col xs={24} md={12}>
                  <Form.Group controlId="defaultDuration">
                    <FloatingLabel label="Default Duration (min)" className="mb-3">
                      <Form.Control
                        type="number"
                        name="defaultDuration"
                        placeholder="Default Duration"
                        value={values.defaultDuration}
                        onChange={handleChange}
                        isInvalid={touched.defaultDuration && !!errors.defaultDuration}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.defaultDuration}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Group controlId="defaultBufferDuration">
                    <FloatingLabel label="Default Buffer Duration (min)" className="mb-3">
                      <Form.Control
                        type="number"
                        name="defaultBufferDuration"
                        placeholder="Default Buffer Duration"
                        value={values.defaultBufferDuration}
                        onChange={handleChange}
                        isInvalid={touched.defaultBufferDuration && !!errors.defaultBufferDuration}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.defaultBufferDuration}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Form.Group>
                </Col>
              </Row>

              <FormSectionDivider />

              {/* Provider Settings */}
              <FormSectionHeader>Provider Settings</FormSectionHeader>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <FormToggleLabel
                  className="me-3"
                  title="Hide Providers"
                  description="Providers will not be shown to customers during booking"
                />
                <div className="form-check form-switch fs-4 flex-shrink-0 ps-0">
                  <input
                    className="form-check-input ms-0 mt-0"
                    type="checkbox"
                    role="switch"
                    style={{ cursor: "pointer" }}
                    checked={values.hideProviders}
                    onChange={() => setFieldValue("hideProviders", !values.hideProviders)}
                  />
                </div>
              </div>

              <BSButton ref={submitRef} type="submit" size="lg" className="d-none" />
            </Form>
          )
        }}
      </Formik>
    </SlideOutModal>
  );
}

export default AddServiceModal;
